import React, { useMemo } from 'react'
import { Box, Flex, Text, Tooltip, useAppTheme } from '@workwhile/ui'
import {
  formatPayBreakdownMinutes,
  PayBreakdownTooltipContent,
} from 'components/PayBreakdownTooltipContent'
import { Work } from '../../types'
import { getShiftStatus } from '../../utils/getShiftStatus'

type WorkerShiftStatusProps = {
  workerId: number
  status: Work['status']
  startedAt?: string
  completedAt?: string
  removedReason?: Work['removedReason']
  timezone: string
  payBreakdown?: Work['payBreakdown']
}

export const WorkerShiftStatus = ({
  workerId,
  status,
  startedAt,
  completedAt,
  removedReason,
  timezone,
  payBreakdown,
}: WorkerShiftStatusProps) => {
  const { colors } = useAppTheme()
  const shiftStatus = useMemo(
    () =>
      getShiftStatus({
        status,
        startedAt,
        completedAt,
        removedReason,
        timezone,
      }),
    [status, startedAt, completedAt, removedReason, timezone]
  )

  if (shiftStatus == null) {
    return null
  }

  const showPayBreakdown =
    shiftStatus.kind === 'time_worked' && payBreakdown != null

  return (
    <Flex alignItems="center" gap={2} flexWrap="wrap">
      <Text color="lightText">
        {shiftStatus.text.split('\n').map((line, index) => (
          <Text key={`shift-status-line-${workerId}-${index}`}>{line}</Text>
        ))}
      </Text>
      {showPayBreakdown ? (
        <Tooltip
          variant="neutral"
          content={<PayBreakdownTooltipContent payBreakdown={payBreakdown} />}
        >
          <Box
            as="span"
            style={{
              display: 'inline-block',
              borderBottom: `1px dotted ${colors.lightText}`,
              cursor: 'help',
            }}
          >
            <Text color="lightText">
              Payable{' '}
              {formatPayBreakdownMinutes(payBreakdown.totalWorkDurationMinutes)}
            </Text>
          </Box>
        </Tooltip>
      ) : null}
    </Flex>
  )
}
